import type { ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { Pressable } from '../../Common/Pressable'
import type { GoalProgressRow, UpcomingDateRow } from '../../../utils/overviewInsights'

export interface GoalsSectionProps {
  goals: GoalProgressRow[]
  upcoming: UpcomingDateRow[]
  onNavigateToAnalysis?: (tab: 'TEAM' | 'WORK') => void
  toolsNode?: ReactNode
}

export const GoalsSection = ({
  goals,
  upcoming,
  onNavigateToAnalysis,
  toolsNode,
}: GoalsSectionProps) => {
  const { t } = useNls()

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done':
      case 'on_track':
        return 'var(--success)'
      case 'at_risk':
        return 'var(--warning)'
      case 'off_track':
      default:
        return 'var(--danger)'
    }
  }

  const formatMonthDay = (endDateMs?: number | null) => {
    if (!endDateMs) return { day: '—', month: '' }
    const d = new Date(endDateMs)
    const day = String(d.getDate()).padStart(2, '0')
    const month = d.toLocaleDateString(undefined, { month: 'short' })
    return { day, month }
  }

  return (
    <div className="overviewCard" data-testid="overview-goals-section">
      {toolsNode}
      <div className="overviewCard__header">
        <h3 className="overviewCard__title">{t('NLSOverviewGoalsDatesTitle')}</h3>
        {onNavigateToAnalysis && (
          <Pressable
            type="button"
            className="overviewCard__link"
            onClick={() => onNavigateToAnalysis('WORK')}
          >
            {t('NLSOverviewWorkAnalysisLink')}
          </Pressable>
        )}
      </div>

      <div className="overviewGoalsSplit">
        {/* Left: Goals */}
        <div>
          {goals.map((goal) => {
            const { day, month } = formatMonthDay(goal.endDateMs)
            const dueText = goal.endDateMs
              ? t('NLSOverviewGoalDuePrefix', { date: `${day} ${month}` })
              : ''
            const statusCol = getStatusColor(goal.status)

            return (
              <div key={goal.id} className="overviewGoalRow">
                <div className="overviewGoalRow__top">
                  <span className="overviewGoalRow__title">{goal.title}</span>
                  <span className="overviewGoalRow__meta">
                    {goal.progress}% {dueText ? `· ${dueText} ` : ''}·{' '}
                    <strong style={{ color: statusCol }}>{goal.status}</strong>
                  </span>
                </div>
                <div className="overviewProgressBar">
                  <div
                    className="overviewProgressBar__fill"
                    style={{
                      width: `${Math.max(0, Math.min(100, goal.progress))}%`,
                      background: statusCol,
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>

        {/* Right: Upcoming dates */}
        <div>
          {upcoming.map((item) => {
            const { day, month } = formatMonthDay(item.endDateMs)
            return (
              <div key={item.id} className="overviewDateRow">
                <div className="overviewDateBadge">
                  <span className="overviewDateBadge__day">{day}</span>
                  <span className="overviewDateBadge__month">{month}</span>
                </div>
                <div>
                  <div className="overviewDateInfo__title">{item.title}</div>
                  <div className="overviewDateInfo__sub">
                    {item.type}{' '}
                    {item.daysUntil != null
                      ? `· ${t('NLSOverviewUpcomingDaysIn', { days: item.daysUntil })}`
                      : ''}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
